import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  Box,
  Typography,
  IconButton,
  Tooltip,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Divider,
  Menu,
  MenuItem,
} from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import CloseIcon from "@mui/icons-material/Close";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { ReactComponent as DropdownIcon } from "../../assets/images/icons/logs/dropdown-arrow.svg";
import AppTable from "../../common/app-table";
import AppChip from "../../common/app-chip";
import AppDropdown from "../../common/app-dropdown";
import ButtonOne from "../../common/normal-button";
import TableSearchInput from "../TableSearchInput";
import { ColorsConstants } from "../../cssUtils/constants";
import typography from "../../cssUtils/typography";
import { useAuth } from "../../hooks/AuthProvider";
import useLogExport from "../../hooks/useLogExport";
import { LEAD_GEN_LEADS, LEAD_GEN_LEADS_EXPORT_PREVIEW, LEAD_GEN_LEADS_EXPORT } from "../../config/urls";
import { formatWithTzLabel } from "../../utils/dateUtils";

// ── Constants ─────────────────────────────────────────────────────────────────

const DEFAULT_ROWS_PER_PAGE = 20;
const SEARCH_DEBOUNCE_MS = 400;

// ── Component ─────────────────────────────────────────────────────────────────

const CustomerLeads = () => {
  const { getValidToken } = useAuth();
  const tenant = localStorage.getItem("tenant");

  const [leads, setLeads] = useState([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [alert, setAlert] = useState({ message: "", severity: "error" });

  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [charCount, setCharCount] = useState(0);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);

  const [filterSource, setFilterSource] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [statusOptions, setStatusOptions] = useState([]);
  const [statusesLoading, setStatusesLoading] = useState(true);
  const [sourceOptions, setSourceOptions] = useState([]);
  const [sourcesLoading, setSourcesLoading] = useState(true);

  const [detailDialog, setDetailDialog] = useState({ open: false, lead: null });
  const [deleteDialog, setDeleteDialog] = useState({ open: false, lead: null });
  const [deleting, setDeleting] = useState(false);

  // Per-row overflow menu
  const [actionMenuAnchorEl, setActionMenuAnchorEl] = useState(null);
  const [selectedRow, setSelectedRow] = useState(null);
  const handleActionClick = (e, row) => {
    e.stopPropagation();
    setActionMenuAnchorEl(e.currentTarget);
    setSelectedRow(row);
  };
  const handleActionClose = () => setActionMenuAnchorEl(null);

  const activeFilterCount = [filterSource, filterStatus].filter(Boolean).length;

  // ── Debounce search input ────────────────────────────────────────────────

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(0);
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [search]);

  // ── Fetch status filter options ──────────────────────────────────────────

  useEffect(() => {
    const fetchStatuses = async () => {
      setStatusesLoading(true);
      try {
        const token = await getValidToken();
        const res = await fetch(`${LEAD_GEN_LEADS}statuses/`, {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
            "X-Tenant-Id": tenant,
          },
        });
        if (!res.ok) throw new Error();
        const data = await res.json();
        setStatusOptions(Array.isArray(data) ? data : []);
      } catch {
        // silently fail — status filter just shows no options
      } finally {
        setStatusesLoading(false);
      }
    };
    fetchStatuses();
  }, []);

  // ── Fetch source filter options ──────────────────────────────────────────

  useEffect(() => {
    const fetchSources = async () => {
      setSourcesLoading(true);
      try {
        const token = await getValidToken();
        const res = await fetch(`${LEAD_GEN_LEADS}sources/`, {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
            "X-Tenant-Id": tenant,
          },
        });
        if (!res.ok) throw new Error();
        const data = await res.json();
        setSourceOptions(Array.isArray(data) ? data : []);
      } catch {
        // silently fail — source filter just shows no options
      } finally {
        setSourcesLoading(false);
      }
    };
    fetchSources();
  }, []);

  // ── Build query params (shared between list and export) ─────────────────

  const buildParams = (includePagination = true) => {
    const params = new URLSearchParams();
    if (debouncedSearch) params.set("search", debouncedSearch);
    if (filterSource) params.set("source", filterSource);
    if (filterStatus) params.set("status", filterStatus);
    if (includePagination) {
      params.set("limit", rowsPerPage);
      params.set("offset", page * rowsPerPage);
    }
    return params;
  };

  // ── Fetch leads ───────────────────────────────────────────────────────────

  const fetchLeads = async () => {
    setLoading(true);
    setAlert({ message: "", severity: "error" });
    try {
      const token = await getValidToken();
      const res = await fetch(`${LEAD_GEN_LEADS}?${buildParams(true).toString()}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
          "X-Tenant-Id": tenant,
        },
      });
      if (!res.ok) throw new Error("Failed to fetch leads");
      const data = await res.json();
      setLeads(data.results || []);
      setCount(data.count || 0);
    } catch (err) {
      setAlert({ message: err.message || "Failed to fetch leads", severity: "error" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLeads();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage, debouncedSearch, filterSource, filterStatus]);

  // ── Filters ───────────────────────────────────────────────────────────────

  const handleResetFilters = () => {
    setFilterSource("");
    setFilterStatus("");
    setPage(0);
  };

  const handleSearchChange = (value) => {
    if (value.length > 50) return;
    setSearch(value);
    setCharCount(value.length);
  };

  // ── Export ────────────────────────────────────────────────────────────────

  const exportFilters = useMemo(
    () => ({ search: debouncedSearch, source: filterSource, status: filterStatus }),
    [debouncedSearch, filterSource, filterStatus],
  );

  const {
    preview: exportPreview,
    jobStatus: exportJobStatus,
    loading: exporting,
    error: exportError,
    triggerExport,
  } = useLogExport(exportFilters, {
    previewEndpoint: LEAD_GEN_LEADS_EXPORT_PREVIEW,
    exportEndpoint: LEAD_GEN_LEADS_EXPORT,
    fallbackFilenamePrefix: "Leads",
  });

  // Surface export failures only once an export attempt finishes, so a failed
  // background preview fetch doesn't raise an alert on page load.
  const wasExportingRef = useRef(false);
  useEffect(() => {
    if (wasExportingRef.current && !exporting) {
      if (exportError) {
        setAlert({ message: exportError, severity: "error" });
      } else if (exportJobStatus?.status === "ready") {
        setAlert({ message: "Your export is ready. The download link has been sent to your email.", severity: "success" });
      }
    }
    wasExportingRef.current = exporting;
  }, [exporting, exportError, exportJobStatus]);

  const handleExport = () => {
    if (!exportPreview) {
      setAlert({ message: "Still calculating export size, please try again in a moment.", severity: "warning" });
      return;
    }
    if (exportPreview.export_mode === "blocked") {
      setAlert({ message: exportPreview.message, severity: "error" });
      return;
    }
    setAlert(
      exportPreview.export_mode === "async"
        ? { message: exportPreview.message, severity: "info" }
        : { message: "", severity: "error" },
    );
    triggerExport();
  };

  // ── Pagination ────────────────────────────────────────────────────────────

  const handlePageChange = (_, newPage) => setPage(newPage);
  const handleRowsPerPageChange = (e) => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setPage(0);
  };

  // ── View / Delete ─────────────────────────────────────────────────────────

  const handleViewDetails = (row) => {
    handleActionClose();
    setDetailDialog({ open: true, lead: row });
  };
  const handleDetailClose = () => setDetailDialog({ open: false, lead: null });

  const handleDeleteClick = (row) => {
    handleActionClose();
    setDeleteDialog({ open: true, lead: row });
  };
  const handleDeleteConfirm = async () => {
    const { lead } = deleteDialog;
    if (!lead) return;
    setDeleting(true);
    try {
      const token = await getValidToken();
      const res = await fetch(`${LEAD_GEN_LEADS}${lead.id}/delete/`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}`, "X-Tenant-Id": tenant },
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error(d?.error || "Failed to delete lead");
      }
      setLeads((prev) => prev.filter((l) => l.id !== lead.id));
      setCount((prev) => Math.max(0, prev - 1));
      setDeleteDialog({ open: false, lead: null });
    } catch (err) {
      setAlert({ message: err.message || "Failed to delete lead", severity: "error" });
      setDeleteDialog({ open: false, lead: null });
    } finally {
      setDeleting(false);
    }
  };

  // ── Table columns ─────────────────────────────────────────────────────────

  const textCell = (value) =>
    value ? (
      <Typography sx={{ ...typography.bodySmall, color: "#1D2330" }}>{value}</Typography>
    ) : (
      <Typography component="span" sx={{ ...typography.bodySmall, color: "#9CA3AF", fontStyle: "italic" }}>
        —
      </Typography>
    );

  const formatPhone = (row) => {
    if (!row.phone) return null;
    // `country_code` is rarely populated on the Lead model — most rows already have the
    // code baked into `phone` (e.g. "+919004890938"). Only apply the "(+cc) number" spacing
    // when country_code is known, stripping it from phone first to avoid double-prefixing.
    if (!row.country_code) return row.phone;
    const cc = String(row.country_code);
    const digitsOnly = row.phone.replace(/^\+/, "");
    const localNumber = digitsOnly.startsWith(cc) ? digitsOnly.slice(cc.length) : digitsOnly;
    return `(+${cc}) ${localNumber}`;
  };

  const truncatedCell = (value, maxWidth = 220) =>
    value ? (
      <Tooltip title={value} arrow placement="top">
        <Typography
          sx={{
            ...typography.bodySmall,
            color: "#1D2330",
            maxWidth,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {value}
        </Typography>
      </Tooltip>
    ) : (
      textCell(null)
    );

  const columns = useMemo(
    () => [
      { id: "name", label: "Name", minWidth: 160, render: (row) => textCell(row.name) },
      { id: "phone", label: "Phone", minWidth: 170, render: (row) => textCell(formatPhone(row)) },
      { id: "email", label: "Email", minWidth: 190, render: (row) => textCell(row.email) },
      { id: "company", label: "Company", minWidth: 160, render: (row) => textCell(row.company) },
      { id: "location", label: "Location", minWidth: 150, render: (row) => textCell(row.location) },
      {
        id: "source",
        label: "Source",
        minWidth: 190,
        render: (row) =>
          row.source ? (
            <AppChip label={row.source} sx={{ bgcolor: "#F0F5FF", color: ColorsConstants.primaryBackgroundColor }} />
          ) : (
            textCell(null)
          ),
      },
      {
        id: "status",
        label: "Status",
        minWidth: 130,
        render: (row) =>
          row.status ? (
            <AppChip label={row.status} sx={{ bgcolor: "#F3F4F6", color: "#374151" }} />
          ) : (
            textCell(null)
          ),
      },
      { id: "owner", label: "Owner", minWidth: 140, render: (row) => textCell(row.owner) },
      { id: "description", label: "Description", minWidth: 220, render: (row) => truncatedCell(row.description) },
      { id: "last_synced_at", label: "Last Synced", minWidth: 180, render: (row) => textCell(formatWithTzLabel(row.last_synced_at_iso)) },
      {
        id: "actions",
        label: "Actions",
        minWidth: 70,
        align: "center",
        render: (row) => (
          <IconButton
            size="small"
            onClick={(e) => handleActionClick(e, row)}
            sx={{ color: "#6C7C93", "&:hover": { bgcolor: "#F0F2F8", color: ColorsConstants.primaryBackgroundColor } }}
          >
            <MoreVertIcon sx={{ fontSize: 20 }} />
          </IconButton>
        ),
      },
    ],
    [],
  );

  const rowSx = (_, index) => ({
    backgroundColor: index % 2 === 0 ? "#F0F5FF" : "#FFFFFF",
    height: "47px",
  });

  const statusMenuItems = statusOptions.map((s) => ({ label: s, value: s }));
  const sourceMenuItems = sourceOptions.map((s) => ({ label: s, value: s }));

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <Box sx={{ backgroundColor: "#FAFAFA", p: 3 }}>

      {/* ── Toolbar ── */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2.5, flexWrap: "wrap" }}>
        <Box sx={{ flexShrink: 0 }}>
          <Typography sx={{ ...typography.h1, color: "#1D2330" }}>Leads</Typography>
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexShrink: 0, ml: "auto" }}>
          <ButtonOne
            variant="outlined"
            onClick={handleExport}
            disabled={exporting}
            startIcon={exporting ? <CircularProgress size={14} sx={{ color: "inherit" }} /> : <FileDownloadOutlinedIcon />}
            title="Export"
          />
        </Box>
      </Box>

      {/* ── Alert ── */}
      {alert.message && (
        <Alert severity={alert.severity} onClose={() => setAlert({ message: "", severity: "error" })} sx={{ mb: 2, borderRadius: "8px" }}>
          {alert.message}
        </Alert>
      )}

      {/* ── Filter bar ── */}
      <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 1, mb: 2, overflowX: "auto" }}>
        <Box sx={{ flex: 1, minWidth: 240 }}>
          <TableSearchInput
            searchQuery={search}
            onSearchChange={(e) => handleSearchChange(e.target.value)}
            charCount={charCount}
            placeholder="Search by name, phone, company…"
            tooltip="Searches by name, phone number, and company"
          />
        </Box>

        <AppDropdown
          endIcon={<DropdownIcon />}
          menuItems={sourceMenuItems}
          selectedValue={filterSource}
          loading={sourcesLoading}
          emptyText="No sources yet"
          onMenuSelect={(value) => { setFilterSource(filterSource === value ? "" : value); setPage(0); }}
          sx={{ whiteSpace: "nowrap", minWidth: 140, px: 1.2 }}
        >
          Source
        </AppDropdown>

        <AppDropdown
          endIcon={<DropdownIcon />}
          menuItems={statusMenuItems}
          selectedValue={filterStatus}
          loading={statusesLoading}
          emptyText="No statuses yet"
          onMenuSelect={(value) => { setFilterStatus(filterStatus === value ? "" : value); setPage(0); }}
          sx={{ whiteSpace: "nowrap", minWidth: 130, px: 1.2 }}
        >
          Status
        </AppDropdown>

        {activeFilterCount > 0 && (
          <ButtonOne
            variant="outlined"
            onClick={handleResetFilters}
            sx={{
              height: 36, px: 2, ...typography.bodyLarge,
              border: "1px solid #DCDFE5", color: "#6C7C93",
              "&:hover": { border: "1px solid #9CA3AF", backgroundColor: "transparent" },
            }}
          >
            Reset
          </ButtonOne>
        )}
      </Box>

      {/* ── Table ── */}
      <AppTable
        columns={columns}
        rows={leads}
        rowKey="id"
        loading={loading}
        onRowClick={handleViewDetails}
        maxHeight={560}
        rowSx={rowSx}
        paperSx={{ border: "none", boxShadow: "none", mb: 0 }}
        emptyMessage="No leads found"
        count={count}
        page={page}
        rowsPerPage={rowsPerPage}
        rowsPerPageOptions={[20, 50, 100]}
        onPageChange={handlePageChange}
        onRowsPerPageChange={handleRowsPerPageChange}
      />

      {/* ── Row overflow menu ── */}
      <Menu
        anchorEl={actionMenuAnchorEl}
        open={Boolean(actionMenuAnchorEl)}
        onClose={handleActionClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
      >
        <MenuItem onClick={() => handleViewDetails(selectedRow)} sx={{ ...typography.bodyLarge, gap: 1 }}>
          <VisibilityOutlinedIcon sx={{ fontSize: 18, color: "#6C7C93" }} /> View Details
        </MenuItem>
        <MenuItem onClick={() => handleDeleteClick(selectedRow)} sx={{ ...typography.bodyLarge, gap: 1, color: "#E05252" }}>
          <DeleteOutlineIcon sx={{ fontSize: 18 }} /> Delete
        </MenuItem>
      </Menu>

      {/* ── Detail Dialog ── */}
      <Dialog open={detailDialog.open} onClose={handleDetailClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: "12px" } }}>
        <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", pb: 1, ...typography.h2, color: "#1D2330" }}>
          Lead Details
          <IconButton size="small" onClick={handleDetailClose} sx={{ color: "#6C7C93" }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 2 }}>
          {detailDialog.lead && (
            <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1.5, backgroundColor: "#F8FAFC", borderRadius: "8px", p: 2 }}>
              {[
                ["Name", detailDialog.lead.name],
                ["Phone", detailDialog.lead.phone],
                ["Country Code", detailDialog.lead.country_code],
                ["Email", detailDialog.lead.email],
                ["Company", detailDialog.lead.company],
                ["Location", detailDialog.lead.location],
                ["Source", detailDialog.lead.source],
                ["Status", detailDialog.lead.status],
                ["Enquiry Type", detailDialog.lead.enquiry_type],
                ["Owner", detailDialog.lead.owner],
                ["Last Synced", detailDialog.lead.last_synced_at_iso ? formatWithTzLabel(detailDialog.lead.last_synced_at_iso) : null],
                ["Received At", detailDialog.lead.created_at_iso ? formatWithTzLabel(detailDialog.lead.created_at_iso) : null],
                ["Description", detailDialog.lead.description, true],
              ].map(([label, value, fullWidth]) => (
                <Box key={label} sx={fullWidth ? { gridColumn: "1 / -1" } : undefined}>
                  <Typography sx={{ ...typography.bodySmall, color: "#6C7C93", mb: 0.25 }}>{label}</Typography>
                  <Typography sx={{ ...typography.bodyLarge, color: "#1D2330", whiteSpace: "pre-wrap" }}>{value || "—"}</Typography>
                </Box>
              ))}
            </Box>
          )}
        </DialogContent>
      </Dialog>

      {/* ── Delete Confirmation ── */}
      <Dialog
        open={deleteDialog.open}
        onClose={() => !deleting && setDeleteDialog({ open: false, lead: null })}
        PaperProps={{ sx: { borderRadius: "12px", minWidth: 360, p: 1 } }}
      >
        <DialogTitle sx={{ ...typography.h2, color: "#1D2330", pb: 1 }}>Delete Lead</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ ...typography.bodyLarge, color: "#6C7C93" }}>
            Are you sure you want to delete this lead? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <ButtonOne
            variant="outlined"
            onClick={() => setDeleteDialog({ open: false, lead: null })}
            disabled={deleting}
            sx={{ height: 36, px: 2, ...typography.bodyLarge, border: "1px solid #DCDFE5", color: "#1D2330", "&:hover": { border: "1px solid #9CA3AF", backgroundColor: "transparent" } }}
          >
            Cancel
          </ButtonOne>
          <ButtonOne
            variant="contained"
            onClick={handleDeleteConfirm}
            disabled={deleting}
            startIcon={deleting ? <CircularProgress size={14} sx={{ color: "#fff" }} /> : null}
            sx={{ height: 36, px: 2, ...typography.bodyLarge, backgroundColor: "#E05252", "&:hover": { backgroundColor: "#C94444" }, "&.Mui-disabled": { backgroundColor: "#E05252", opacity: 0.6 } }}
          >
            {deleting ? "Deleting…" : "Delete"}
          </ButtonOne>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CustomerLeads;
